import { type ReactNode } from "react";
import { Loader2, Plug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { connectPlatform } from "@/lib/hubAuth";

// Shared scaffolding for platform views. Every view renders one of three
// states: connect CTA (no usable platform token), skeleton (loading), or
// content. Keeping the CTA here means the copy and connect flow stay
// identical across surfaces.

export function ConnectPlatformCard() {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="glass glass-border card-glow max-w-sm rounded-xl p-6 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
          <Plug className="h-6 w-6 text-primary" />
        </div>
        <h2 className="mb-1 text-base font-semibold">Connect your OpZero account</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Sign in with OpZero to see the projects, assistants, and MCP servers
          you have deployed on opzero.sh.
        </p>
        <Button variant="hero" onClick={() => void connectPlatform()}>
          Connect
        </Button>
      </div>
    </div>
  );
}

export function ViewSection({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("mb-6", className)}>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="glass-border flex flex-col items-center gap-3 rounded-xl border border-dashed p-8 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
      {action}
    </div>
  );
}

export function ViewLoading() {
  return (
    <div className="flex flex-1 items-center justify-center p-10">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>
  );
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(t).toLocaleDateString();
}
